import "./util/initBugsnag.ts";
import "../common/polyfills.ts";
import type { ReactNode } from "react";
import { createRoot } from "react-dom/client";
import api from "./api/index.ts";
import { Controller } from "./components/Controller/index.tsx";
import { router, RouteNotFoundError } from "./router/index.ts";
import type { Env } from "../common/types.ts";
import {
	EMAIL_ADDRESS,
	ERROR_MESSAGE_UNDEFINED_SEASON,
	GAME_NAME,
	ERROR_MESSAGE_ONE_TAB,
	WEBSITE_ROOT,
	ERROR_MESSSAGE_LEAGUE_NOT_FOUND,
} from "../common/constants.ts";
import Bugsnag from "@bugsnag/browser";
import { LeagueNotFoundMessage } from "./components/LeagueNotFoundMessage.tsx";
import { ErrorBoundary } from "./components/ErrorBoundary.tsx";
import { ads } from "./util/ads.ts";
import { genStaticPage } from "./util/genStaticPage.tsx";
import { analyticsEvent } from "./util/analyticsEvent.ts";
import { compareVersions } from "./util/compareVersions.ts";
import { promiseWorker } from "./util/promiseWorker.ts";
import { routes } from "./util/routes.ts";
import { unregisterServiceWorkers } from "./util/unregisterServiceWorkers.ts";
import { safeLocalStorage } from "./util/safeLocalStorage.ts";
import { showNotification } from "./util/showNotification.ts";
import { toWorker } from "./util/toWorker.ts";

const handleVersion = () => {
	window.addEventListener("storage", (e) => {
		if (e.key === "bbgmVersionConflict") {
			const bbgmVersionStored = safeLocalStorage.getItem("bbgmVersion");

			if (
				bbgmVersionStored &&
				compareVersions(bbgmVersionStored, window.bbgmVersion) === 1
			) {
				showNotification({
					type: "error",
					text: `A newer version of ${GAME_NAME} was just opened in another tab. Please reload this tab to load the same version here.`,
					persistent: true,
				});
			}
		} else if (e.key === "theme") {
			if (window.themeCSSLink) {
				window.themeCSSLink.href = window.getThemeFilename(window.getTheme());
			}
		}
	});
	analyticsEvent("app_version", {
		app_version: window.bbgmVersion,
	});

	window.withGoodUI?.();

	void toWorker("main", "ping", undefined).then(() => {
		window.withGoodWorker?.();
	});

	// Check if there are other tabs open with a different version
	const bbgmVersionStored = safeLocalStorage.getItem("bbgmVersion");

	if (bbgmVersionStored) {
		const cmpResult = compareVersions(window.bbgmVersion, bbgmVersionStored);

		if (cmpResult === 1) {
			// This version is newer than another tab's - send a signal to the other tabs
			let conflictNum = Number.parseInt(
				// @ts-expect-error
				safeLocalStorage.getItem("bbgmVersionConflict"),
			);

			if (Number.isNaN(conflictNum)) {
				conflictNum = 1;
			} else {
				conflictNum += 1;
			}

			safeLocalStorage.setItem("bbgmVersion", window.bbgmVersion);
			safeLocalStorage.setItem("bbgmVersionConflict", String(conflictNum));
		} else if (cmpResult === -1) {
			// This version is older than another tab's
			console.log(window.bbgmVersion, bbgmVersionStored);
			console.log(
				`This version of ${GAME_NAME} (${window.bbgmVersion}) is older than one you already played (${bbgmVersionStored}). This should never happen, so please email ${EMAIL_ADDRESS} with any info about how this error occurred.`,
			);

			// Don't block
			unregisterServiceWorkers();
		}
	} else {
		// Initial load, store version for future comparisons
		safeLocalStorage.setItem("bbgmVersion", window.bbgmVersion);
	}
};

const setupEnv = async () => {
	// Heartbeat, used to keep only one tab open at a time for browsers where we have to use a Web
	// Worker due to lack of Shared Worker support (currently just Safari). Uses sessionStorage
	// rather than a global variable to persist over page reloads, otherwise it'd be a race
	// condition to distinguish between reloading the page and opening it in two tabs.
	let heartbeatID = sessionStorage.getItem("heartbeatID");

	if (heartbeatID === null || heartbeatID === undefined) {
		heartbeatID = Math.random().toString(16).slice(2);
		sessionStorage.setItem("heartbeatID", heartbeatID);
	}

	const env: Env = {
		bbgmVersion: window.bbgmVersion,
		enableLogging: window.enableLogging,
		heartbeatID,
		mobile: window.mobile,
		useSharedWorker: window.useSharedWorker,
	};
	await toWorker("main", "init", env);
};

const render = () => {
	const container = document.getElementById("content");
	const root = createRoot(container!);
	root.render(
		<ErrorBoundary>
			<Controller />
		</ErrorBoundary>,
	);
};

const getUrlForAnalytics = (path: string) => {
	// Track page_view here rather than in routeMatched so logged title is correct
	const pagePath = path.replace(/^\/l\/\d+/, "/l/0");

	return `${location.origin}${pagePath}`;
};

const setupRoutes = async () => {
	let initialLoad = true;
	await router.start({
		routeMatched: ({ context }) => {
			if (!context.state.noTrack) {
				if (initialLoad) {
					initialLoad = false;
				} else {
					// This will only do something if ads are already initialized, so it's (mostly) safe to call here even though this could be an error page, since at least it won't show on an error page for the initial pageview
					ads.refreshAll();

					// There is no way to fix the URL for initial pageviews, according to the current docs, but I guess that's not a big deal for me, we'll see
					ads.trackPageview(getUrlForAnalytics(context.path));
				}
			}
		},
		navigationEnd: ({ context, error }) => {
			if (error) {
				let errorMessage: ReactNode;

				// Some "errors" are expected and need either some special UI or are not worth logging
				if (error instanceof RouteNotFoundError) {
					errorMessage = "Page not found.";
				} else if (error.message === ERROR_MESSSAGE_LEAGUE_NOT_FOUND) {
					errorMessage = <LeagueNotFoundMessage />;
				} else if (error.name === "VersionError") {
					errorMessage = (
						<>
							<p>{error.message}</p>
							<p>
								Please{" "}
								<a
									href={`https://${WEBSITE_ROOT}/manual/faq/#latest-version`}
									target="_blank"
								>
									make sure you have the latest version of the game loaded
								</a>
								.
							</p>
						</>
					);

					unregisterServiceWorkers();
				} else if (
					error.message === ERROR_MESSAGE_ONE_TAB ||
					error.message === ERROR_MESSAGE_UNDEFINED_SEASON
				) {
					errorMessage = error.message;
				} else {
					// Log any error not explicitly handled above
					Bugsnag.notify(error);

					console.error("Error from view:");
					console.error(error);

					errorMessage = error.message;
				}

				const ErrorPage = (
					<>
						{typeof errorMessage === "string" ? (
							<p>{errorMessage}</p>
						) : (
							errorMessage
						)}
					</>
				);
				const errorPage = genStaticPage("error", "Error", ErrorPage, false);
				errorPage(context);
			} else if (!context.state.noTrack) {
				// If this is not an error page, initialize ads. init() will do nothing if it's already initialized
				ads.init();
			}

			if (!context.state.noTrack && window.enableLogging) {
				// https://developers.google.com/analytics/devguides/collection/ga4/views?client_type=gtag
				analyticsEvent("page_view", {
					page_location: getUrlForAnalytics(context.path),
				});
			}
		},
		routes,
	});
};

(async () => {
	promiseWorker.register(([name, ...params]: any) => {
		if (!Object.hasOwn(api, name)) {
			throw new Error(
				`API call to nonexistant UI function "${name}" with params ${JSON.stringify(
					params,
				)}`,
			);
		}

		// https://github.com/microsoft/TypeScript/issues/21732
		// @ts-expect-error
		return api[name](...params);
	});
	handleVersion();
	await setupEnv();
	render();
	await setupRoutes();

	const { initServiceWorker } = await import("./util/initServiceWorker.tsx");
	await initServiceWorker();
})();
